import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import strings from 'common/Translation/Translate';
import axiosInstance from 'utils/axios';
import { identifiers } from 'utils/constants/identifiers';
import { URLS } from 'utils/constants/urls';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

export interface DashboardChartsApiResponse {
  days: number;
  dateLabels: string[];
  revenueDailyConfirmed: number[];
  revenueByVenue: { label: string; values: number[] }[];
  revenueByEventType: { label: string; values: number[] }[];
  revenueBySource: { label: string; values: number[] }[];
  bookingsConfirmedPerDay: number[];
  bookingsTentativePerDay: number[];
  footnote?: string;
}

const CHART_COLORS = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f', '#00838f', '#5d4037', '#455a64'];

const MAX_CHART_RANGE_DAYS = 365;

type PresetDays = 14 | 30 | 60;
type AppliedWindow = { type: 'preset'; days: PresetDays } | { type: 'range'; start: string; end: string };

function formatInrChart(value: number) {
  return formatCurrencyWithSymbol(value);
}

function shortDayLabel(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

interface DashboardChartsSectionProps {
  onError: (e: unknown) => void;
}

const DashboardChartsSection: FC<DashboardChartsSectionProps> = ({ onError }) => {
  const theme = useTheme();
  const [applied, setApplied] = useState<AppliedWindow>({ type: 'preset', days: 30 });
  const [draftStart, setDraftStart] = useState<Dayjs>(() => dayjs().subtract(29, 'day'));
  const [draftEnd, setDraftEnd] = useState<Dayjs>(() => dayjs());
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardChartsApiResponse | null>(null);
  const [trendTab, setTrendTab] = useState<'revenue' | 'booking'>('revenue');
  const [revenueTab, setRevenueTab] = useState(0);

  useEffect(() => {
    if (applied.type === 'range') {
      setDraftStart(dayjs(applied.start));
      setDraftEnd(dayjs(applied.end));
    }
  }, [applied]);

  const fetchCharts = useCallback(async () => {
    setLoading(true);
    try {
      const params =
        applied.type === 'preset'
          ? { days: applied.days }
          : { startDate: applied.start, endDate: applied.end };
      const { data: res } = await axiosInstance.get<DashboardChartsApiResponse>(URLS.dashboard_chart_data, {
        params,
      });
      setData(res);
    } catch (e) {
      onError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [applied, onError]);

  useEffect(() => {
    fetchCharts();
  }, [fetchCharts]);

  const toggleValue: PresetDays | 'custom' = applied.type === 'preset' ? applied.days : 'custom';

  const onWindowToggle = (_: React.MouseEvent<HTMLElement>, v: PresetDays | 'custom' | null) => {
    if (v == null) return;
    if (v === 'custom') {
      const spanDays = applied.type === 'preset' ? applied.days : 30;
      const end = dayjs();
      const start = end.subtract(spanDays - 1, 'day');
      setDraftStart(start);
      setDraftEnd(end);
      setApplied({
        type: 'range',
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
      });
      setRangeError(null);
      return;
    }
    setApplied({ type: 'preset', days: v });
    setRangeError(null);
  };

  const applyCustomRange = () => {
    if (!draftStart?.isValid() || !draftEnd?.isValid()) {
      setRangeError(strings.dashboard_trends_range_invalid);
      return;
    }
    if (draftEnd.isBefore(draftStart, 'day')) {
      setRangeError(strings.dashboard_trends_range_invalid);
      return;
    }
    const endCapped = draftEnd.isAfter(dayjs(), 'day') ? dayjs() : draftEnd;
    if (endCapped.isBefore(draftStart, 'day')) {
      setRangeError(strings.dashboard_trends_range_invalid);
      return;
    }
    const spanDays = endCapped.diff(draftStart, 'day') + 1;
    if (spanDays > MAX_CHART_RANGE_DAYS) {
      setRangeError(strings.dashboard_trends_range_too_long);
      return;
    }
    setApplied({
      type: 'range',
      start: draftStart.format('YYYY-MM-DD'),
      end: endCapped.format('YYYY-MM-DD'),
    });
    setRangeError(null);
  };

  const xLabels = useMemo(() => (data?.dateLabels ?? []).map(shortDayLabel), [data?.dateLabels]);

  const revenueOption = useMemo(() => {
    if (!data?.dateLabels?.length) return null;
    const tab = revenueTab;

    let series: any[];
    const palette = tab === 0 ? [theme.palette.primary.main] : CHART_COLORS;

    if (tab === 0) {
      const color = palette[0];
      series = [
        {
          name: 'Revenue (confirmed)',
          type: 'line',
          data: data.revenueDailyConfirmed ?? [],
          smooth: 0.35,
          color,
          itemStyle: { color },
          lineStyle: { color, width: 2 },
          areaStyle: { color: `${color}22` },
          showSymbol: false,
          emphasis: { disabled: true },
        },
      ];
    } else {
      const list = tab === 1 ? data.revenueByVenue : tab === 2 ? data.revenueByEventType : data.revenueBySource;
      if (!list?.length) return null;
      series = list.map((s, i) => {
        const color = palette[i % palette.length];
        return {
          name: s.label,
          type: 'line',
          data: s.values ?? [],
          smooth: 0.35,
          color,
          itemStyle: { color },
          lineStyle: { color, width: 2 },
          showSymbol: false,
          emphasis: { disabled: true },
        };
      });
    }

    return {
      color: palette,
      animation: true,
      animationDuration: 400,
      grid: { left: 50, right: 20, top: tab > 0 ? 40 : 20, bottom: 30, containLabel: false },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: 'rgba(148,163,184,0.2)',
        borderWidth: 1,
        textStyle: { fontFamily: 'system-ui', fontSize: 12, color: '#e2e8f0' },
        extraCssText: 'border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.15);',
        valueFormatter: (v: number) => formatInrChart(v),
      },
      legend:
        tab > 0
          ? {
              show: true,
              top: 0,
              textStyle: { fontSize: 11 },
              itemWidth: 10,
              itemHeight: 10,
              icon: 'roundRect',
            }
          : { show: false },
      xAxis: {
        type: 'category',
        data: xLabels,
        axisLabel: { fontSize: 10, rotate: 0, interval: 'auto' },
        axisLine: { lineStyle: { color: theme.palette.divider } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 10, formatter: (v: number) => formatInrChart(v) },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series,
    };
  }, [data, revenueTab, theme.palette.primary.main, theme.palette.divider, xLabels]);

  const bookingOption = useMemo(() => {
    if (!data?.dateLabels?.length) return null;
    const confirmedColor = theme.palette.success.main;
    const tentativeColor = theme.palette.warning.main;
    return {
      color: [confirmedColor, tentativeColor],
      animation: true,
      animationDuration: 400,
      grid: { left: 40, right: 20, top: 40, bottom: 30, containLabel: false },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: 'rgba(148,163,184,0.2)',
        borderWidth: 1,
        textStyle: { fontFamily: 'system-ui', fontSize: 12, color: '#e2e8f0' },
        extraCssText: 'border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.15);',
      },
      legend: {
        show: true,
        top: 0,
        textStyle: { fontSize: 11 },
        itemWidth: 10,
        itemHeight: 10,
        icon: 'roundRect',
      },
      xAxis: {
        type: 'category',
        data: xLabels,
        axisLabel: { fontSize: 10, rotate: 0, interval: 'auto' },
        axisLine: { lineStyle: { color: theme.palette.divider } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 10, formatter: (v: number) => String(Math.round(v)) },
        splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false },
        minInterval: 1,
      },
      series: [
        {
          name: 'Confirmed',
          type: 'line',
          data: data.bookingsConfirmedPerDay ?? [],
          smooth: 0.35,
          color: confirmedColor,
          itemStyle: { color: confirmedColor },
          lineStyle: { color: confirmedColor, width: 2 },
          areaStyle: { color: `${confirmedColor}18` },
          showSymbol: false,
          emphasis: { disabled: true },
        },
        {
          name: 'Tentative',
          type: 'line',
          data: data.bookingsTentativePerDay ?? [],
          smooth: 0.35,
          color: tentativeColor,
          itemStyle: { color: tentativeColor },
          lineStyle: { color: tentativeColor, width: 2 },
          areaStyle: { color: `${tentativeColor}18` },
          showSymbol: false,
          emphasis: { disabled: true },
        },
      ],
    };
  }, [data, theme.palette.success.main, theme.palette.warning.main, theme.palette.divider, xLabels]);

  const cardShellSx = {
    width: '100%',
    height: '100%',
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column' as const,
    p: 3,
    borderRadius: '14px',
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: 'background.paper',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    boxSizing: 'border-box' as const,
  };

  const today = dayjs();

  return (
    <Box sx={cardShellSx}>
      <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
        <ShowChartRoundedIcon sx={{ fontSize: 22, color: 'primary.main' }} />
        <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Trends</Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={toggleValue}
          onChange={onWindowToggle}
          sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.5, fontSize: 12, fontWeight: 700 } }}>
          <ToggleButton value={14}>14d</ToggleButton>
          <ToggleButton value={30}>30d</ToggleButton>
          <ToggleButton value={60}>60d</ToggleButton>
          <ToggleButton value="custom">{strings.dashboard_trends_custom}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {applied.type === 'range' && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 1.25, width: '100%' }}>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <DesktopDatePicker
              label={strings.bookingDetailsStartDate}
              value={draftStart}
              onChange={v => {
                setDraftStart(v ?? draftStart);
                setRangeError(null);
              }}
              inputFormat={identifiers.date_format}
              maxDate={draftEnd.isValid() ? draftEnd : today}
              shouldDisableDate={date => date.isAfter(today, 'day')}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { minHeight: 40 },
                    '& .MuiInputBase-input': { py: 1, fontSize: 13 },
                    '& .MuiInputLabel-root': { fontSize: 12 },
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <DesktopDatePicker
              label={strings.bookingDetailsEndDate}
              value={draftEnd}
              onChange={v => {
                setDraftEnd(v ?? draftEnd);
                setRangeError(null);
              }}
              inputFormat={identifiers.date_format}
              minDate={draftStart ?? undefined}
              maxDate={today}
              shouldDisableDate={date =>
                Boolean(draftStart && date.isBefore(draftStart, 'day')) || date.isAfter(today, 'day')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { minHeight: 40 },
                    '& .MuiInputBase-input': { py: 1, fontSize: 13 },
                    '& .MuiInputLabel-root': { fontSize: 12 },
                  }}
                />
              )}
            />
          </Box>
          <Button
            variant="contained"
            size="medium"
            onClick={applyCustomRange}
            sx={{
              flexShrink: 0,
              minHeight: 40,
              px: 2,
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>
            {strings.dashboard_trends_apply_range}
          </Button>
        </Stack>
      )}

      {rangeError && (
        <Typography sx={{ fontSize: 12, color: 'error.main', mb: 1 }} role="alert">
          {rangeError}
        </Typography>
      )}

      {data?.footnote && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1, flexShrink: 0 }}>{data.footnote}</Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={trendTab}
          onChange={(_, v) => setTrendTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            mb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 40,
              py: 0.75,
              fontSize: 12.5,
              fontWeight: 900,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&.Mui-selected': {
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                marginBottom: '-2px',
              },
            },
          }}>
          <Tab value="revenue" label="Revenue Trend" />
          <Tab value="booking" label="Booking Trend" />
        </Tabs>

        {trendTab === 'revenue' ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Tabs
              value={revenueTab}
              onChange={(_, v) => setRevenueTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                mb: 1,
                flexShrink: 0,
                '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 12, fontWeight: 800 },
              }}>
              <Tab label="Daily" />
              <Tab label="Venue" />
              <Tab label="Segment" />
              <Tab label="Source" />
            </Tabs>
            <Box sx={{ flex: 1, minHeight: 220, position: 'relative' }}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: 1, height: '100%', minHeight: 220 }} />
              ) : !revenueOption && revenueTab > 0 ? (
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 220,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}>
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                    No revenue in this breakdown for the selected window.
                  </Typography>
                </Box>
              ) : revenueOption ? (
                <ReactECharts option={revenueOption} style={{ height: '100%', width: '100%', minHeight: 220 }} notMerge />
              ) : (
                <Skeleton variant="rounded" sx={{ borderRadius: 1, height: '100%', minHeight: 220 }} />
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 650, mb: 1, flexShrink: 0 }}>
              Line items per day by start date — confirmed vs tentative
            </Typography>
            <Box sx={{ flex: 1, minHeight: 220, position: 'relative' }}>
              {loading || !bookingOption ? (
                <Skeleton variant="rounded" sx={{ borderRadius: 1, height: '100%', minHeight: 220 }} />
              ) : (
                <ReactECharts option={bookingOption} style={{ height: '100%', width: '100%', minHeight: 220 }} notMerge />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DashboardChartsSection;
